import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

interface VenueStatsProps {
  stats: {
    soundTech: number;
    technicalRider: number;
    merchandising: number;
    catering: number;
    ticketing: number;
    backline: number;
    audioRecording: number;
    posterDesign: number;
    total: number;
  };
}

const items = [
  { key: "soundTech" as const, label: "Técnico de sonido" },
  { key: "technicalRider" as const, label: "Rider técnico" },
  { key: "merchandising" as const, label: "Zona merchandising" },
  { key: "catering" as const, label: "Catering" },
  { key: "ticketing" as const, label: "Ticketing obligatorio" },
  { key: "backline" as const, label: "Backline disponible" },
  { key: "audioRecording" as const, label: "Grabación audio" },
  { key: "posterDesign" as const, label: "Cartelería" },
];

function getStatus(yes: number, total: number) {
  if (total === 0)
    return { label: "Sin datos de la comunidad", type: "no-data" as const };
  const pct = Math.round((yes / total) * 100);
  if (pct >= 75) return { label: "Suele estar disponible", type: "high" as const };
  if (pct >= 25) return { label: "Varía según el evento", type: "mid" as const };
  return { label: "La comunidad no lo reporta", type: "low" as const };
}

function getStatusColor(type: "high" | "mid" | "low" | "no-data") {
  switch (type) {
    case "high":
      return {
        badge: "bg-success/15 text-success border-success/30",
        text: "text-success",
        bar: "[&>div]:bg-success",
      };
    case "mid":
      return {
        badge: "bg-warning/15 text-warning border-warning/30",
        text: "text-warning",
        bar: "[&>div]:bg-warning",
      };
    case "low":
      return {
        badge: "bg-destructive/15 text-destructive border-destructive/30",
        text: "text-destructive",
        bar: "[&>div]:bg-destructive",
      };
    case "no-data":
      return {
        badge: "bg-muted text-muted-foreground border-border",
        text: "text-muted-foreground/50",
        bar: "",
      };
  }
}

export function VenueStats({ stats }: VenueStatsProps) {
  return (
    <section>
      <h2 className="mb-1 font-display text-xl font-bold">
        Información colaborativa
      </h2>
      <p className="mb-6 text-xs text-muted-foreground">
        Basado en {stats.total} review{stats.total !== 1 ? "s" : ""} de usuarios
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        {items.map(({ key, label }) => {
          const yes = stats[key];
          const total = stats.total;
          const pct = total > 0 ? Math.round((yes / total) * 100) : 0;
          const { label: statusLabel, type } = getStatus(yes, total);
          const colors = getStatusColor(type);
          const isNoData = type === "no-data";

          return (
            <div key={key} className="space-y-2">
              {/* Header row: label + badge */}
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold leading-tight">{label}</p>
                {!isNoData && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-2 py-0.5 font-bold ${colors.badge}`}
                  >
                    {pct}%
                  </Badge>
                )}
              </div>

              {/* Status text */}
              <p
                className={`text-xs font-medium ${
                  isNoData ? "text-muted-foreground/50 italic" : colors.text
                }`}
              >
                {statusLabel}
              </p>

              {/* Progress bar */}
              <Progress
                value={isNoData ? 0 : pct}
                className={`h-2 ${isNoData ? "opacity-20" : ""} ${colors.bar}`}
              />

              {/* Detail */}
              {!isNoData && (
                <p className="text-[11px] text-muted-foreground">
                  Sí en {yes} de {total} review{total !== 1 ? "s" : ""} ({pct}%)
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
